import { Navigate, Route, Routes, useLocation, useNavigate } from "react-router";
import { useState, type ReactNode } from "react";
import { Dashboard, LoginPage, ParticipantArea, SignupPage } from "./app/ConferisApp.js";
import { api } from "./app/api.js";
import { apiErrorMessage } from "./api/client.js";
import { OrganizerMobileNav } from "./app/OrganizerMobileNav.js";
import { ParticipantMobileNav } from "./app/ParticipantMobileNav.js";
import { EnquiryPage } from "./marketing/EnquiryPage.js";
import { BackToLanding } from "./design/components/BackToLanding.js";
import { StartupLoader } from "./design/components/StartupLoader.js";
import { DemoNotice } from "./design/components/DemoNotice.js";
import { LandingPage, PublicFooter, PublicHeader } from "./marketing/LandingPage.js";
import { RequireSession } from "./session/RequireSession.js";
import { useSession } from "./session/SessionProvider.js";
import { PasswordResetPage } from "./session/PasswordResetPage.js";
import { InvitationResponsePage } from "./app/reviewer/InvitationResponsePage.js";
import { PublicSubmissionEntryPage } from "./app/submission/PublicSubmissionEntryPage.js";
import { PublicConferenceSitePage } from "./app/public/PublicConferenceSitePage.js";
import { PublicRegistrationPage } from "./app/registration/PublicRegistrationPage.js";

function safeNext(search: string) {
  const candidate = new URLSearchParams(search).get("next");
  return candidate?.startsWith("/") && !candidate.startsWith("//") ? candidate : "/app";
}

function LoginRoute() {
  const { user, setUser } = useSession();
  const navigate = useNavigate();
  const location = useLocation();
  if (user) return <Navigate to={safeNext(location.search)} replace />;
  const returnTo = safeNext(location.search);
  return <LoginPage returnTo={returnTo} onAuthenticated={(nextUser) => { setUser(nextUser); navigate(returnTo, { replace: true }); }} />;
}

function SignupRoute() {
  const { user, setUser } = useSession();
  const navigate = useNavigate();
  const location = useLocation();
  const returnTo = safeNext(location.search);
  if (user) return <Navigate to={returnTo} replace />;
  const authorSignup = returnTo.startsWith("/c/") && returnTo.endsWith("/submit");
  const attendeeSignup = returnTo.startsWith("/c/") && returnTo.endsWith("/register");
  return <SignupPage
    title={authorSignup ? "Create your author account" : attendeeSignup ? "Create your attendee account" : undefined}
    description={authorSignup ? "After signup, you will return to this conference and can start your submission." : attendeeSignup ? "After signup, you will return to this conference and complete your registration." : undefined}
    loginHref={`/login?next=${encodeURIComponent(returnTo)}`}
    onCompleted={(result) => {
    if (result.authenticated) { setUser(result.user); navigate(returnTo, { replace: true }); }
    else { setUser(null); navigate(`/verify-email?email=${encodeURIComponent(result.user.email)}&next=${encodeURIComponent(returnTo)}`, { replace: true }); }
  }} />;
}

function AuthorSubmissionRoute() {
  const { user } = useSession();
  const location = useLocation();
  if (!user) {
    const destination = `${location.pathname}${location.search}`;
    return <Navigate to={`/signup?next=${encodeURIComponent(destination)}`} replace />;
  }
  return <PublicSubmissionEntryPage />;
}

function AttendeeRegistrationRoute() {
  const { user } = useSession();
  const location = useLocation();
  if (!user) {
    const destination = `${location.pathname}${location.search}`;
    return <Navigate to={`/signup?next=${encodeURIComponent(destination)}`} replace />;
  }
  return <PublicRegistrationPage />;
}

function InformationPage({ title, description, children }: { title: string; description: string; children?: ReactNode }) {
  return <div className="mk-site"><PublicHeader /><main className="info-page"><BackToLanding /><p>Conferis</p><h1>{title}</h1><span>{description}</span>{children}</main><PublicFooter /></div>;
}

function VerifyEmailPage() {
  const location = useLocation();
  const email = new URLSearchParams(location.search).get("email");
  const returnTo = safeNext(location.search);
  return <InformationPage title="Check your email" description={email ? `We sent the verification step for ${email}. Complete it before logging in.` : "Complete the verification step in your email before logging in."}><a className="mk-link-button mk-link-button--primary" href={`/login?next=${encodeURIComponent(returnTo)}`}>Return to login</a></InformationPage>;
}

function ApplicationRoute() {
  const { user, logout } = useSession();
  const navigate = useNavigate();
  if (!user) return null;
  const leave = () => void logout().then(() => navigate("/", { replace: true }));
  return <><Dashboard user={user} onLogout={leave} /><OrganizerMobileNav conferenceContext={null} section="overview" onLogout={leave} /></>;
}

function AccountSecurityPage() {
  const { user, setUser } = useSession();
  const [verifying, setVerifying] = useState(false);
  const [verificationError, setVerificationError] = useState("");
  if (!user) return <Navigate to="/login?next=%2Faccount%2Fsecurity" replace />;
  const demoNeedsVerification = import.meta.env.VITE_CONFERIS_ENVIRONMENT === "demo" && !user.email_verified;
  const verifyForDemo = async () => {
    setVerifying(true);
    setVerificationError("");
    try {
      const result = await api.verifyDemoEmail();
      setUser(result.user);
    } catch (reason) {
      setVerificationError(apiErrorMessage(reason, "Demo verification could not be completed."));
    } finally {
      setVerifying(false);
    }
  };
  return <div className="mk-site"><PublicHeader /><main className="security-overview"><BackToLanding href="/app" label="Back to workspace" /><p className="security-overview__eyebrow">Account security</p><h1>Keep access to your workspace safe.</h1><p className="security-overview__lead">Conferis asks for a second check before protected conference work. Security controls are available on every plan.</p>{demoNeedsVerification ? <section className="security-overview__status security-overview__status--demo"><span aria-hidden="true">1</span><div><strong>Confirm this demo account</strong><p>This demo-only confirmation unlocks MFA and protected test workflows. It does not verify ownership outside this demo.</p>{verificationError ? <p className="security-overview__error" role="status">{verificationError}</p> : null}</div><button type="button" className="mk-link-button mk-link-button--primary" disabled={verifying} onClick={() => void verifyForDemo()}>{verifying ? "Verifying…" : "Verify for this demo"}</button></section> : <section className={`security-overview__status${user.mfa_enabled ? " is-active" : ""}`}><span aria-hidden="true">{user.mfa_enabled ? "✓" : "2"}</span><div><strong>{user.mfa_enabled ? "Multi-factor authentication is active" : "Add a second sign-in step"}</strong><p>{user.mfa_enabled ? "Authenticator and recovery options can be reviewed at any time." : "Use an authenticator app or a passkey. Setup usually takes less than two minutes."}</p></div><a className="mk-link-button mk-link-button--primary" href="/accounts/2fa/">{user.mfa_enabled ? "Manage security" : "Set up MFA"}</a></section>}<div className="security-overview__notes"><article><strong>Recovery access</strong><p>Generate one-time codes and store them somewhere separate from your device.</p></article><article><strong>Protected actions</strong><p>Conference creation and elevated administration require verified, recent authentication.</p></article></div></main><PublicFooter /></div>;
}

function NotFoundPage() {
  return <main className="route-message"><p>404</p><h1>Page not found</h1><a href="/">Return to Conferis</a></main>;
}

function ReviewerInvitationRoute() {
  const search = new URLSearchParams(useLocation().search);
  return <InvitationResponsePage token={search.get("token") ?? ""} finishExisting={search.get("finish") === "1"} />;
}

export function AppRouter() {
  const { ready } = useSession();
  if (!ready) return <StartupLoader delayed />;

  return (
    <>
      {import.meta.env.VITE_CONFERIS_ENVIRONMENT === "demo" ? <DemoNotice /> : null}
      <Routes>
      <Route index element={<LandingPage />} />
      <Route path="features" element={<Navigate to="/#products" replace />} />
      <Route path="pricing" element={<Navigate to="/#pricing" replace />} />
      <Route path="security" element={<InformationPage title="Security by default" description="Conferis separates tenant, role, relationship, workflow, and entitlement decisions. Security controls do not depend on package level." />} />
      <Route path="contact" element={<Navigate to="/enquire" replace />} />
      <Route path="enquire" element={<EnquiryPage />} />
      <Route path="login" element={<LoginRoute />} />
      <Route path="signup" element={<SignupRoute />} />
      <Route path="verify-email" element={<VerifyEmailPage />} />
      <Route path="password-reset" element={<PasswordResetPage />} />
      <Route path="reviewer-invitations/accept" element={<ReviewerInvitationRoute />} />
      <Route path="sites/:organizationId/:publicSlug" element={<PublicConferenceSitePage />} />
      <Route path="c/:organizationId/:publicSlug/submit" element={<AuthorSubmissionRoute />} />
      <Route path="c/:organizationId/:publicSlug/register" element={<AttendeeRegistrationRoute />} />
      <Route path="account/security" element={<AccountSecurityPage />} />
      <Route path="staff" element={<InformationPage title="Staff access" description="Operator access requires configured MFA and is not available through the customer preview."><a className="mk-link-button mk-link-button--secondary" href="/">Return home</a></InformationPage>} />
      <Route element={<RequireSession />}>
        <Route path="app/*" element={<ApplicationRoute />} />
        <Route path="my/*" element={<ParticipantRoute />} />
      </Route>
      <Route path="*" element={<NotFoundPage />} />
      </Routes>
    </>
  );
}

function ParticipantRoute() {
  const section = useLocation().pathname.split("/").filter(Boolean)[1] || "submissions";
  return <><ParticipantArea /><ParticipantMobileNav section={section} /></>;
}
